// lib/authStorage.js — the ONE place that reads / writes role & admin
// session tokens in browser storage.
//
// Security notes (Session 71 code-quality pass):
//  • Tokens used to be read with ad-hoc `localStorage.getItem(...)` calls in
//    ten different files. Every access now goes through this module so the
//    storage policy can be audited and changed in one place.
//  • Admin (platform super-admin) sessions are TAB-SCOPED ONLY
//    (sessionStorage) — they are never written to disk-backed localStorage.
//  • Role sessions (staff / owner / kitchen / bar / retail) use a two-phase
//    policy: a fresh login is "pending" and lives in tab-scoped
//    sessionStorage; once the dashboard renders it is "confirmed" and
//    promoted to device-scoped localStorage. Device persistence is a hard
//    product requirement — kitchen/bar tablets go through dozens of
//    screen-off / soft-reboot cycles per shift and must not ask for a PIN
//    every time (see Session 31.20). Memory-only or encrypted-with-IDB-key
//    storage was evaluated and rejected: IDB is unreliable on the Android
//    WebViews this app ships on and would re-introduce the "waiters get
//    kicked out" incident. The remaining exposure (same-origin script
//    reading storage) can only be closed by httpOnly cookies, which needs a
//    backend auth redesign and is tracked as a separate task.
//  • Nothing here logs or echoes token values.

export const ROLE_SESSION_KEYS = Object.freeze({
  staff: "restaurant-smart-menu-staff-session",
  owner: "restaurant-smart-menu-owner-session",
  admin: "restaurant-smart-menu-admin-session",
  kitchen: "restaurant-smart-menu-kitchen-session",
  bar: "restaurant-smart-menu-bar-session",
  retail: "restaurant-smart-menu-retail-session",
});

/** Roles whose sessions may be persisted on the device (admin excluded). */
export const DEVICE_ROLES = Object.freeze(["owner", "staff", "kitchen", "bar", "retail"]);

function _store(kind) {
  try {
    if (typeof window === "undefined") return null;
    return kind === "tab" ? window.sessionStorage : window.localStorage;
  } catch (_) {
    return null;
  }
}
function _storesFor(scope) {
  // "tab" → sessionStorage only; "device" → localStorage only;
  // "any" → tab first, then device (most-recent login wins).
  if (scope === "tab") return [_store("tab")];
  if (scope === "device") return [_store("device")];
  return [_store("tab"), _store("device")];
}
function _parse(raw) {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch (_) { return null; }
}

/** Raw JSON string for a role session, or null. */
export function readRoleSessionRaw(role, scope = "any") {
  const key = ROLE_SESSION_KEYS[role];
  if (!key) return null;
  for (const store of _storesFor(scope)) {
    if (!store) continue;
    try {
      const raw = store.getItem(key);
      if (raw) return raw;
    } catch (_) { /* storage unavailable */ }
  }
  return null;
}

/** Parsed role session object, or null. */
export function readRoleSession(role, scope = "any") {
  return _parse(readRoleSessionRaw(role, scope));
}

/** Write a role session. scope: "tab" | "device" | "both". */
export function writeRoleSession(role, value, scope = "tab") {
  const key = ROLE_SESSION_KEYS[role];
  if (!key) return false;
  const payload = typeof value === "string" ? value : JSON.stringify(value);
  const targets = scope === "both" ? [_store("tab"), _store("device")] : _storesFor(scope);
  let ok = false;
  for (const store of targets) {
    if (!store) continue;
    try { store.setItem(key, payload); ok = true; } catch (_) { /* quota / disabled */ }
  }
  return ok;
}

/** Remove a role session from the given scope ("tab" | "device" | "both"). */
export function removeRoleSession(role, scope = "both") {
  const key = ROLE_SESSION_KEYS[role];
  if (!key) return;
  const targets = scope === "both" ? [_store("tab"), _store("device")] : _storesFor(scope);
  for (const store of targets) {
    if (!store) continue;
    try { store.removeItem(key); } catch (_) { /* noop */ }
  }
}

export function hasRoleSession(role, scope = "any") {
  return !!readRoleSessionRaw(role, scope);
}

/**
 * Iterate stored sessions. `fn(role, session, storeKind)` is called for each
 * stored entry (storeKind is "tab" or "device"). Return `true` from fn to
 * stop early. Unparseable entries are passed as `null` sessions so callers
 * can clean them up.
 */
export function forEachStoredSession(fn, { roles = Object.keys(ROLE_SESSION_KEYS), scope = "any" } = {}) {
  const kinds = scope === "tab" ? ["tab"] : scope === "device" ? ["device"] : ["tab", "device"];
  for (const role of roles) {
    const key = ROLE_SESSION_KEYS[role];
    if (!key) continue;
    for (const kind of kinds) {
      const store = _store(kind);
      if (!store) continue;
      let raw = null;
      try { raw = store.getItem(key); } catch (_) { raw = null; }
      if (!raw) continue;
      if (fn(role, _parse(raw), kind) === true) return;
    }
  }
}

/**
 * First session matching `predicate(session, role)` across the given roles
 * (in order) and scope. Returns `{ role, session }` or null.
 */
export function findRoleSession(predicate, { roles = DEVICE_ROLES, scope = "any" } = {}) {
  let found = null;
  forEachStoredSession((role, session) => {
    if (session && predicate(session, role)) { found = { role, session }; return true; }
    return false;
  }, { roles, scope });
  return found;
}

const isOnlineToken = (t) => typeof t === "string" && t.length > 0 && !t.startsWith("offline:");

/** First real (non-offline) bearer token across role sessions, or null. */
export function findOnlineRoleToken({ roles = Object.keys(ROLE_SESSION_KEYS), scope = "any" } = {}) {
  const hit = findRoleSession((s) => isOnlineToken(s.token), { roles, scope });
  return hit ? hit.session.token : null;
}

// ─── Admin (platform super-admin) — strictly tab-scoped ────────────────────

export function readAdminSession() {
  return readRoleSession("admin", "tab");
}
export function writeAdminSession(payload) {
  // Defensive: make sure no legacy device-wide admin session lingers.
  removeRoleSession("admin", "device");
  return writeRoleSession("admin", payload, "tab");
}
export function clearAdminSession() {
  removeRoleSession("admin", "both");
}
